import { Sparkles, Bug, Zap } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { useI18n } from '../i18n';
import type { ReleaseVersion, ReleaseSectionType } from '../release-notes-types';

interface WhatsNewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versions: ReleaseVersion[];
  currentVersion: string | undefined;
}

const SECTION_CONFIG: Record<
  ReleaseSectionType,
  {
    icon: typeof Sparkles;
    labelKey:
      | 'releaseNotes.sectionNew'
      | 'releaseNotes.sectionFix'
      | 'releaseNotes.sectionImprovement';
  }
> = {
  new: { icon: Sparkles, labelKey: 'releaseNotes.sectionNew' },
  fix: { icon: Bug, labelKey: 'releaseNotes.sectionFix' },
  improvement: { icon: Zap, labelKey: 'releaseNotes.sectionImprovement' },
};

export function WhatsNewDialog({
  open,
  onOpenChange,
  versions,
  currentVersion,
}: WhatsNewDialogProps) {
  const { t, locale } = useI18n();

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date);
    } catch {
      return dateStr;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t('releaseNotes.title')}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh]">
          <div className="space-y-4 pr-4">
            {versions.map((version) => {
              const isCurrent = version.version === currentVersion;
              return (
                <div
                  key={version.version}
                  className={`rounded-lg border p-4 ${isCurrent ? 'border-2 border-accent' : 'border-border'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-base">
                      v{version.version}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {formatDate(version.date)}
                    </span>
                  </div>
                  {isCurrent && (
                    <span className="inline-block text-xs bg-accent/20 text-accent-foreground px-2 py-0.5 rounded mb-2">
                      {t('releaseNotes.currentVersion')}
                    </span>
                  )}
                  {version.sections.map((section) => {
                    const config = SECTION_CONFIG[section.type];
                    const Icon = config.icon;
                    return (
                      <div key={section.type} className="mt-2">
                        <div className="flex items-center gap-1.5 text-sm font-medium mb-1">
                          <Icon size={14} />
                          {t(config.labelKey)}
                        </div>
                        <ul className="list-disc list-inside text-sm text-muted-foreground space-y-0.5">
                          {(locale === 'en' && section.itemsEn?.length ? section.itemsEn : section.items).map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {versions.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                {t('releaseNotes.empty')}
              </p>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
